import React from "react";
import { Checkbox, Table } from "@weezevent/nacre";
import PropTypes from "prop-types";
import { ROW_SELECTION_PROP_TYPES } from "../../proptypes";
import StopPropagation from "../../../stop-propagation";
import Style from "./data-row.module.scss";
import classNames from "classnames";

const DataRow = React.memo(
	({
		rowData,
		columns,
		computeRowIsDisabled,
		onRowClick,
		selectionEnabled,
		selected,
		onChangeSelection,
		customRowClass,
		customCellClassFn
	}) => {
		const rows = React.useMemo(
			() =>
				columns.map((column, index) => {
					const style = column.width
						? { width: column.width, maxWidth: column.width }
						: undefined;

					const className = classNames(Style["table-data"], {
						[Style["table-data--clickable"]]: !!onRowClick
					});

					return (
						<td
							key={column.key}
							className={[
								className,
								customCellClassFn(rowData, column.key)
							]
								.filter(Boolean)
								.join(" ")}
							style={style}
						>
							<div style={{ textAlign: column.align || "left" }}>
								{column.render
									? column.render(
											rowData[column.key],
											rowData,
											index
									  )
									: rowData[column.key]}
							</div>
						</td>
					);
				}),
			[columns, rowData, customCellClassFn, onRowClick]
		);

		return (
			<Table.Tr
				disabled={computeRowIsDisabled(rowData)}
				active={selected}
				onClick={onRowClick ? () => onRowClick(rowData) : undefined}
				className={customRowClass}
			>
				{selectionEnabled && (
					<td
						className={Style["checkbox"]}
						onClick={e => e.stopPropagation()}
					>
						<StopPropagation>
							<Checkbox
								checked={selected}
								onChange={(_, { checked }) =>
									onChangeSelection(checked)
								}
							/>
						</StopPropagation>
					</td>
				)}
				{rows}
			</Table.Tr>
		);
	}
);

DataRow.displayName = "DataRow";

DataRow.propTypes = {
	rowData: PropTypes.object.isRequired,
	columns: ROW_SELECTION_PROP_TYPES.Columns.isRequired,
	computeRowIsDisabled: PropTypes.func,
	onRowClick: PropTypes.func,
	selectionEnabled: PropTypes.bool,
	selected: PropTypes.bool,
	onChangeSelection: PropTypes.func,
	customRowClass: PropTypes.string,
	customCellClassFn: PropTypes.func
};

DataRow.defaultProps = {
	computeRowIsDisabled: () => false,
	onRowClick: null,
	selectionEnabled: false,
	selected: false,
	onChangeSelection: () => {},
	customRowClass: undefined,
	customCellClassFn: () => undefined
};

export default DataRow;
